import React, { useContext } from "react";
import MProgressBar from "../MProgressBar/MProgressBar";
import { AuthContext } from "../../services/AuthContext";

import "./MExpandedPlayer.css";
import { useAlert } from "../../services/AlertProvider";
import usePlayerUtils from "../../hooks/usePlayerUtils";
import { useAudioVisualizer } from "../../hooks/useAudioVisualizer";

function MExpandedPlayer({ wrapChildClick, onTabChevron, expandedOpacity, listRef, togglePlay }) {
    const {
        audioRef,
        currentTrack,
        nowPlaying,
        playbackState,
        audioTimeMs,
        setCurrentTimeSec,
        playbackTimeMs,
        currentLiveMs,
        bufferStartMs,
        progressBarPercent,
        durationPercent,
        formatTimestamp,
        setManualTrack,
        setIsSeeking,
        parseDuration,
        setTimeShiftOffsetSec,
        timeShiftOffsetSec,
    } = usePlayerUtils();

    const { isLoggedIn, setUserModalOpen } = useContext(AuthContext);

    const canvasRef = useAudioVisualizer(audioRef);

    const { showAlert } = useAlert();

    const artist = currentTrack?.artist;
    const songTitle = currentTrack?.title;
    const coverUrl = currentTrack?.coverUrl;
    const startedAt = currentTrack?.startedAt;

    const handleLiveBtn = (e) => {
        const audio = audioRef.current;
        if (!audio) return;

        setTimeShiftOffsetSec(0);

        audio.currentTime = timeShiftOffsetSec + audio.currentTime;
        setCurrentTimeSec(timeShiftOffsetSec + audio.currentTime);

        handleResumeLive();
    }

    const handleResumeLive = () => {
        setIsSeeking(false);
        setManualTrack(null);
    };

    const isNext = (startedAt) => {
        if (playbackState !== "playing" || audioTimeMs <= 0 || startedAt === null || startedAt === "") { return false }

        const startedAtNext = startedAt
        const staredAtMs = new Date(startedAtNext).getTime();
        const isNext = staredAtMs > audioTimeMs + timeShiftOffsetSec * 1000;

        return isNext
    }

    const historySeek = (staredAt, duration) => {
        const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};
        if (!staredAt || !duration || staredAt === "" || duration === "") return

        if (isNext(staredAt)) {
            showAlert("You can't play this song yet!")
            return;
        }

        const audio = audioRef.current;
        if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;

        // if (!isLoggedIn) {
        //     setUserModalOpen(true);
        //     return;
        // }

        const staredAtMs = new Date(staredAt).getTime();

        const bufferStartMs = audioTimeMs - (maxTimeShiftSeconds - timeShiftOffsetSec) * 1000;
        const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
        const percent = (staredAtMs - bufferStartMs) / (maxTimeShiftSeconds * 1000);
        const desiredMs = realCurrentTime - maxTimeShiftSeconds + percent * maxTimeShiftSeconds;

        if (desiredMs <= 0) {
            showAlert("You can't play this song anymore!")
            return;
        }

        const newOffset = realCurrentTime - desiredMs;
        setTimeShiftOffsetSec(newOffset);

        audio.currentTime = desiredMs;
        setCurrentTimeSec(desiredMs);

        const currentSong = history.find(song => {
            const start = new Date(song.startedAt).getTime();
            return staredAtMs >= start;
        });

        if (currentSong) {
            setManualTrack(currentSong || null);
            setIsSeeking(true);
        }
    };

    const performSeek = (percent) => {
        // if (!isLoggedIn) {
        //     setUserModalOpen(true);
        //     return;
        // }

        const { maxTimeShiftSeconds, currentLiveTime, history } = nowPlaying || {};

        const audio = audioRef.current;
        if (!audio || !maxTimeShiftSeconds || !currentLiveTime || !history?.length) return;

        const currentLiveMs = new Date(currentLiveTime).getTime();
        const bufferStartMs = currentLiveMs - maxTimeShiftSeconds * 1000;
        const bufferEndMs = currentLiveMs;
        const desiredMs = bufferStartMs + percent * (bufferEndMs - bufferStartMs);

        const seekSec = (desiredMs - bufferStartMs) / 1000;
        const realCurrentTime = timeShiftOffsetSec + audio.currentTime;
        const currentSeekSec = realCurrentTime - maxTimeShiftSeconds + seekSec;

        const newOffset = realCurrentTime - currentSeekSec;
        setTimeShiftOffsetSec(newOffset);

        audio.currentTime = currentSeekSec;
        setCurrentTimeSec(currentSeekSec);

        const seekTimestamp = bufferStartMs + currentSeekSec * 1000;

        const currentSong = history.find(song => {
            const start = new Date(song.startedAt).getTime();
            const end = start + parseDuration(song.duration) * 1000;
            return seekTimestamp >= start && seekTimestamp < end;
        });

        if (currentSong) {
            setManualTrack(currentSong || null);
            setIsSeeking(true);
        }
    };

    const getProgressColor = (percent) => {
        const p = Math.min(Math.max(percent, 0), 1);

        const redBase = p < 0.5 ? 255 : Math.round(255 - (p - 0.5) * 2 * 255);
        const greenBase = p < 0.5 ? Math.round(p * 2 * 255) : 255;
        const blueBase = 0;

        const grayFactor = 0.6; // higher = more grayish
        const red = Math.round(redBase * (1 - grayFactor) + 100 * grayFactor);
        const green = Math.round(greenBase * (1 - grayFactor) + 100 * grayFactor);
        const blue = Math.round(blueBase * (1 - grayFactor) + 100 * grayFactor);

        return `linear-gradient(
        rgba(${red},${green},${blue},0.4),
        rgba(${red},${green},${blue},0.08),
        rgba(${red},${green},${blue},0.4)
        )`;
    };

    const extendedPlayerPlay = () => {
        const modifiedCoverUrl = coverUrl?.includes("default")
            ? coverUrl.replace(".png", "-mid.png")
            : coverUrl;

        return (
            <div className="expanded-player">
                <canvas
                    ref={canvasRef}
                    width={340}
                    height={80}
                    className="expanded-analyzer"
                />
                <div className="cover-info-btn">
                    <div className="extended-btn">
                        <div className="extended-player-btn">
                            <div className="progress-ring" style={{ "--percent": `${durationPercent() * 100}%` }}>
                                <button className="m-play-btn" onClick={wrapChildClick(() => togglePlay())}>
                                    {playbackState === "playing"
                                        ? <i className="fas fa-pause"></i>
                                        : <i className="fas fa-play"></i>
                                    }
                                </button>
                            </div>

                        </div>
                    </div>
                    <div className="extended-info">
                        <div className="extended-song-info">
                            <div className="song-title">{songTitle}</div>
                            <div className="artist">{artist}</div>
                        </div>
                    </div>
                    <div className="extended-cover">
                        <img className="extended-img-cover extended-fade" src={modifiedCoverUrl} alt="Cover" />
                    </div>
                </div>
                <div className="status-progerss">
                    <div className="expanded-times">
                        <div className="m-time">{bufferStartMs ? formatTimestamp(bufferStartMs) : "--:--:--"}</div>
                        <div className="m-time">{currentLiveMs ? formatTimestamp(currentLiveMs) : "--:--:--"}</div>
                    </div>
                    <div className="expanded-progress">
                        <MProgressBar progressPercent={progressBarPercent / 100} onSeek={performSeek} />
                    </div>
                    <div className="m-time m-playbeck-time">
                        <div>
                            {playbackTimeMs ? new Date(playbackTimeMs).toLocaleTimeString("en-GB", { hour12: false }) : "--:--:--"}
                        </div>
                    </div>
                </div>
                <div className="underline"></div>
            </div>
        );
    };

    const expandedPlayerHistory = () => {
        const { history } = nowPlaying || {};
        const songHistory = history || [];

        const liveBtnClassName = timeShiftOffsetSec <= 0 || playbackState === "stopped" ? "m-live-btn-disabled m-live-button" : "m-live-button";

        return (
            <div className="expanded-history-main">
                <button className={liveBtnClassName} onClick={wrapChildClick(() => handleLiveBtn())} disabled={timeShiftOffsetSec <= 0 || playbackState === "stopped"} >
                    <div className="m-live-text">GO LIVE</div>
                </button>
                <div className="expanded-history-title">
                    <div className="histoty-title-main">
                        <div>HISTORY</div>
                    </div>
                    <div className="histoty-title-second">
                        <div>TAP TRACK TO PLAY</div>
                    </div>
                </div>
                <div className="expanded-history-contetnt">
                    <div className="inner-history-content">
                        <div className="contetnt-list">
                            <ul
                                ref={listRef}
                                className="scrollable-list" >
                                {songHistory.map((song, idx) => {
                                    const prevSong = songHistory[idx - 1];
                                    const isLastInPlaylist =
                                        !prevSong || song.playlistName !== prevSong.playlistName;

                                    return (
                                        <React.Fragment key={idx}>
                                            {isLastInPlaylist && song.playlistName && (
                                                <li className="playlist-separator">
                                                    <span className="separator-label">{song.playlistName}</span>
                                                </li>
                                            )}
                                            <li className="m-track-item">
                                                {(playbackState === "playing" || playbackState === "paused") && startedAt === song.startedAt
                                                    && <>
                                                        <div
                                                            className="m-list-song-filled"
                                                            style={{
                                                                width: `${durationPercent() * 100}%`,
                                                                background: playbackState === "playing"
                                                                    ? getProgressColor(durationPercent())
                                                                    : "linear-gradient(rgba(128, 128, 128, 0.459), rgba(128, 128, 128, 0.093), rgba(128, 128, 128, 0.459))",
                                                            }}>

                                                        </div>
                                                        <div className="m-list-song-end" style={{ left: `${durationPercent() * 100}%` }}></div>
                                                    </>}
                                                <button className="m-list-song-link contetnt-item" onClick={wrapChildClick(() => historySeek(song.startedAt, song.duration))} >
                                                    <div className="contetnt-item-track">
                                                        <div className="contetnt-item-title">{song.title}</div>
                                                        <div className="contetnt-item-artist">{song.artist}</div>
                                                    </div>
                                                    <div className="contetnt-item-play">
                                                        {isNext(song.startedAt)
                                                            ? <div className="up-next">UP NEXT</div>
                                                            : playbackState === "playing" && startedAt === song.startedAt
                                                                ? <>
                                                                    <div className="contetnt-item-play-duration">{song.duration}</div>
                                                                    <i className="fas fa-play"></i>
                                                                </>
                                                                : playbackState === "paused" && startedAt === song.startedAt &&
                                                                <>
                                                                    <div className="contetnt-item-play-duration">{song.duration}</div>
                                                                    <i className="fas fa-pause"></i>
                                                                </>}
                                                    </div>
                                                </button>
                                            </li>
                                        </React.Fragment>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    return (
        <div className="player-expanded" style={{ opacity: expandedOpacity }} >
            <div className="chevron-down"><button onClick={onTabChevron} className="chevron-btn"><i className="fa-solid fa-chevron-down"></i></button></div>
            {extendedPlayerPlay()}
            {expandedPlayerHistory()}
        </div>
    );
};

export default React.memo(MExpandedPlayer);
